import { useState } from 'react';

import { toast } from 'react-toastify';
import styled from 'styled-components';

import { colors } from 'constants/colors';

import { GetDetailedProductQuery, LocationType, useUnassignLocationFromProductMutation } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { AddLocationPopup } from 'pages/Products/ProductDetails/components/AddLocationPopup';

type DetailedProduct = NonNullable<GetDetailedProductQuery['detailedProduct']>;

export const LocationsSection = ({ product }: { product: DetailedProduct }) => {
    const { _id: productId, locations } = product;

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [unassignLocation, { loading: unassignLoading }] = useUnassignLocationFromProductMutation();

    const handleRemoveLocation = async (datamatrix: string, warehouseColumnId: string) => {
        const { data } = await unassignLocation({
            variables: {
                productId,
                datamatrix,
                warehouseColumnId,
            },
            refetchQueries: ['GET_DETAILED_PRODUCT'],
        });

        if (data?.unassignLocationFromProduct?.error) {
            toast.error(data.unassignLocationFromProduct.error);
        } else {
            toast.success(`Emplacement ${datamatrix} retiré`);
        }
    };

    return (
        <SectionContainer title="Emplacements entrepôt" isInitiallyOpen={false}>
            <LocationsContainer>
                {locations.length > 0 ? (
                    locations.map((loc) => {
                        const isPicking = (loc.locationType ?? LocationType.Picking) === LocationType.Picking;
                        return (
                            <LocationRow key={loc._id}>
                                <LocationBadge $isPicking={isPicking}>{isPicking ? 'P' : 'R'}</LocationBadge>
                                <LocationDatamatrix>{loc.datamatrix}</LocationDatamatrix>
                                {loc.warehouseColumn?.name ? (
                                    <WarehouseTag>{loc.warehouseColumn.name}</WarehouseTag>
                                ) : null}
                                <RemoveButton
                                    type="button"
                                    disabled={unassignLoading}
                                    onClick={() => handleRemoveLocation(loc.datamatrix, loc.warehouseColumnId!)}
                                >
                                    ✕
                                </RemoveButton>
                            </LocationRow>
                        );
                    })
                ) : (
                    <EmptyText>Aucun emplacement</EmptyText>
                )}

                <TotemPrimaryButton type="button" onClick={() => setIsPopupOpen(true)}>
                    Ajouter un emplacement
                </TotemPrimaryButton>

                <AddLocationPopup isOpen={isPopupOpen} setIsOpen={setIsPopupOpen} productId={productId} />
            </LocationsContainer>
        </SectionContainer>
    );
};

const LocationsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 10px;
`;

const LocationRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid ${colors.pantinGrey};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;

const LocationBadge = styled.span<{ $isPicking: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ $isPicking }) => ($isPicking ? colors.orange : colors.blue)};
    color: white;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
`;

const LocationDatamatrix = styled.span`
    flex: 1;
    font-size: 14px;
    font-weight: 600;
`;

const WarehouseTag = styled.span`
    font-size: 12px;
    color: ${colors.pantinGrey};
    background-color: ${({ theme }) => theme.backgroundColor};
    padding: 2px 8px;
    border-radius: 4px;
`;

const RemoveButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid ${colors.red};
    border-radius: 50%;
    background: transparent;
    color: ${colors.red};
    font-size: 14px;
    cursor: pointer;
    flex-shrink: 0;

    &:hover:not(:disabled) {
        background-color: ${colors.red};
        color: white;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const EmptyText = styled.span`
    font-style: italic;
    color: ${colors.pantinGrey};
    font-size: 14px;
`;
